(() => {
  const STORAGE_KEY = 'showroom-bundle-tray';

  const normalize = (value) =>
    String(value || '')
      .toLowerCase()
      .replace(/pounds?/g, 'lb')
      .replace(/[#]/g, '')
      .replace(/\s+/g, '')
      .trim();

  const sizeKey = (value) => {
    const raw = String(value || '').toLowerCase();
    if (/full\s*bag/.test(raw)) return 'fullbag';
    const match = raw.match(/(\d+(?:\.\d+)?)\s*(?:#|lbs?|pounds?)/);
    if (match) return `${match[1]}lb`;
    return normalize(value);
  };

  const compactKey = (value) =>
    String(value || '')
      .toLowerCase()
      .replace(/&/g, 'and')
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();

  const DEFAULT_CONTINENT_RULES = [
    { label: 'Africa', keywords: ['africa'] },
    { label: 'Latin America', keywords: ['latin america', 'south america', 'central america'] },
    {
      label: 'Asia & Oceania',
      keywords: ['asia', 'oceania', 'oceana', 'pacific', 'middle east'],
    },
  ];

  const DEFAULT_PROCESS_RULES = [
    { label: 'Washed', keywords: ['wash'] },
    { label: 'Natural', keywords: ['natural', 'dry'] },
  ];

  const parseGroupRules = (raw) =>
    String(raw || '')
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean)
      .map((line) => {
        const splitAt = line.indexOf(':');
        if (splitAt === -1) {
          return { label: line, keywords: [line] };
        }
        const label = line.slice(0, splitAt).trim();
        const keywords = line
          .slice(splitAt + 1)
          .split(',')
          .map((part) => part.trim())
          .filter(Boolean);
        return { label, keywords: keywords.length ? keywords : [label] };
      })
      .filter((group) => group.label);

  const asList = (value) => {
    if (value == null || value === '') return [];
    if (Array.isArray(value)) {
      return value.flatMap(asList).filter(Boolean);
    }
    if (typeof value === 'object') {
      if (value.value != null) return asList(value.value);
      if (Array.isArray(value.values)) return asList(value.values);
      return [];
    }
    return String(value)
      .split('|')
      .map((part) => part.trim())
      .filter(Boolean);
  };

  const parseTiers = (raw) => {
    if (Array.isArray(raw)) {
      return raw
        .map((tier) => ({
          qty: Number(tier.qty ?? tier.quantity ?? tier.minQuantity ?? 0),
          percent: Number(tier.percent ?? tier.value ?? 0),
        }))
        .filter((tier) => tier.qty > 0)
        .sort((a, b) => a.qty - b.qty);
    }

    return String(raw || '')
      .split('|')
      .map((chunk) => chunk.trim())
      .filter(Boolean)
      .map((chunk) => {
        const [qty, percent] = chunk.split(/[:/]/);
        return { qty: Number(qty), percent: Number(percent) };
      })
      .filter((tier) => tier.qty > 0)
      .sort((a, b) => a.qty - b.qty);
  };

  const money = (cents) => {
    if (typeof window.formatMoney === 'function') {
      return window.formatMoney(cents);
    }
    const format = window.theme?.moneyFormat || '${{amount}}';
    const amount = (Number(cents) / 100).toFixed(2);
    return format.replace(/\{\{\s*amount\s*\}\}/, amount).replace(/\{\{\s*amount_no_decimals\s*\}\}/, String(Math.round(cents / 100)));
  };

  const newRowId = () => `row-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

  const normalizeTray = (tray) => {
    if (Array.isArray(tray)) return tray;
    if (!tray || typeof tray !== 'object') return [];
    return Object.values(tray).flatMap((item) => {
      const count = Math.max(1, Number(item.qty || 1));
      return Array.from({ length: count }, () => ({
        rowId: newRowId(),
        productId: item.productId,
        variantId: item.variantId,
        title: item.title,
        image: item.image,
        price: item.price,
      }));
    });
  };

  const readStorage = () => {
    try {
      return JSON.parse(sessionStorage.getItem(STORAGE_KEY) || '{}');
    } catch (error) {
      return {};
    }
  };

  const writeStorage = (payload) => {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    } catch (error) {
      /* ignore quota */
    }
  };

  class ShowroomBundleBuilder extends HTMLElement {
    connectedCallback() {
      const configNode = this.querySelector('[data-bundle-config]');
      if (!configNode) return;

      try {
        this.config = JSON.parse(configNode.textContent);
      } catch (error) {
        this.config = {};
      }
      this.config.strings = this.config.strings || {};

      this.sizes = (this.config.sizes || []).map((size) => ({
        ...size,
        tiers: parseTiers(size.tiers),
        uniqueRef: size.uniqueRef || this.matchAppstleRef(size),
      }));
      this.filters = this.config.filters || [];
      this.products = this.config.products || [];
      this.excludeTags = (this.config.excludeTags || []).map(normalize);

      this.state = {
        sizeId: this.sizes.find((size) => size.default)?.id || this.sizes[0]?.id || '',
        search: '',
        selected: {},
        tray: [],
        loading: true,
      };

      const stored = readStorage();
      if (stored.sizeId && this.sizes.some((size) => size.id === stored.sizeId)) {
        this.state.sizeId = stored.sizeId;
        this.state.tray = normalizeTray(stored.tray);
      }

      this.filters.forEach((filter) => {
        this.state.selected[filter.id] = [];
      });

      this.render();
      this.bind();
      this.loadAllProducts();
    }

    async loadAllProducts() {
      const handle = this.config.collectionHandle;
      if (!handle) {
        this.state.loading = false;
        this.render();
        return;
      }

      const root = (window.theme?.routes?.root_url || '/').replace(/\/$/, '');
      const merged = new Map((this.products || []).map((product) => [String(product.id), product]));

      try {
        let page = 1;
        let pages = 1;
        while (page <= pages && page <= 20) {
          const url = `${root}/collections/${handle}?view=bundle-data&page=${page}`;
          // Do not send Accept: application/json — Shopify then returns collection
          // metadata instead of the bundle-data template, so later pages never load.
          const response = await fetch(url);
          const text = await response.text();
          const start = text.indexOf('{');
          const end = text.lastIndexOf('}');
          if (start === -1 || end === -1) break;
          const payload = JSON.parse(text.slice(start, end + 1));
          if (!Array.isArray(payload.products)) break;
          pages = Number(payload.pages || 1);
          payload.products.forEach((product) => merged.set(String(product.id), product));
          page += 1;
        }
        this.products = [...merged.values()];
      } catch (error) {
        /* keep whatever Liquid already provided */
      }

      if (!this.products.length) {
        this.products = this.config.products || [];
      }

      this.state.loading = false;
      this.render();
    }

    appstleRules() {
      let rules = this.config.appstleRules;
      if (typeof rules === 'string') {
        try {
          rules = JSON.parse(rules);
        } catch (error) {
          rules = [];
        }
      }
      if (Array.isArray(rules)) return rules;
      if (rules && typeof rules === 'object') {
        if (Array.isArray(rules.bundle_rules)) return rules.bundle_rules;
        if (Array.isArray(rules.rules)) return rules.rules;
        return [rules];
      }
      return [];
    }

    matchAppstleRule(size) {
      const rules = this.appstleRules();
      const ref = String(size?.uniqueRef || '').trim();
      if (ref) {
        const exact = rules.find(
          (rule) => String(rule.uniqueRef || rule.unique_ref || '').trim() === ref
        );
        if (exact) return exact;
      }

      const haystack = [size?.uniqueRef, size?.checkoutName, size?.label]
        .filter(Boolean)
        .map(normalize);

      return (
        rules.find((rule) => {
          const status = String(rule.status || '').toUpperCase();
          if (status && status !== 'ACTIVE') return false;
          const names = [
            rule.uniqueRef,
            rule.unique_ref,
            rule.name,
            rule.checkoutDiscountName,
            rule.checkout_discount_name,
          ]
            .filter(Boolean)
            .map(normalize);
          return names.some((name) => haystack.some((item) => name.includes(item) || item.includes(name)));
        }) || null
      );
    }

    matchAppstleRef(size) {
      const rule = this.matchAppstleRule(size);
      return rule?.uniqueRef || rule?.unique_ref || '';
    }

    shouldClearCart(rule) {
      const value =
        rule?.clearCartOnCheckout ??
        rule?.clearCart ??
        rule?.clear_cart_on_checkout ??
        rule?.isClearCart;
      if (value === true || value === 1) return true;
      const text = String(value || '').toUpperCase();
      return text === 'TRUE' || text === 'ENABLED' || text === 'YES';
    }

    usesCartDrawer() {
      const type = String(window.theme?.settings?.cartType || '').toLowerCase();
      return type === 'modal';
    }

    afterAddDestination(rule) {
      const redirect = String(
        rule?.bundleRedirect ||
          rule?.bundle_redirect ||
          rule?.redirect ||
          rule?.redirectTo ||
          rule?.redirect_to ||
          'CART'
      )
        .toUpperCase()
        .replace(/[\s-]+/g, '_');
      const custom =
        rule?.customRedirectURL ||
        rule?.customRedirectUrl ||
        rule?.custom_redirect_url ||
        rule?.customRedirect ||
        '';
      const cartUrl = window.theme?.routes?.cart_url || '/cart';

      if (redirect === 'NONE' || redirect === 'NO_REDIRECT') {
        return { type: 'none' };
      }
      if (redirect === 'CHECKOUT') {
        return { type: 'url', href: '/checkout' };
      }

      const wantsDrawer =
        redirect === 'CART_DRAWER' ||
        redirect === 'DRAWER' ||
        redirect === 'MODAL' ||
        redirect === 'POPUP' ||
        redirect === 'MINI_CART' ||
        redirect === 'SLIDE' ||
        redirect === 'SLIDE_CART' ||
        redirect === 'AJAX_CART';
      const wantsCart =
        wantsDrawer ||
        redirect === 'CART' ||
        redirect === 'CART_PAGE' ||
        redirect === 'VIEW_CART';

      if (redirect === 'CUSTOM' && custom) {
        const path = String(custom).replace(window.location.origin, '').split('?')[0];
        const isCartUrl = path === cartUrl || path === '/cart' || /\/cart\/?$/.test(path);
        if (isCartUrl) {
          return this.usesCartDrawer() ? { type: 'drawer' } : { type: 'url', href: cartUrl };
        }
        return { type: 'url', href: custom };
      }

      if (wantsCart && (this.usesCartDrawer() || wantsDrawer)) {
        return { type: 'drawer' };
      }
      if (wantsCart) {
        return { type: 'url', href: cartUrl };
      }
      return { type: 'url', href: cartUrl };
    }

    openCartDrawer() {
      document.dispatchEvent(new CustomEvent('theme:open:cart-drawer'));
      try {
        if (window.Alpine && Alpine.store('modals')) {
          Alpine.store('modals').open('cart');
        }
      } catch (error) {
        /* drawer store not ready */
      }
    }

    currentSize() {
      return this.sizes.find((size) => size.id === this.state.sizeId) || this.sizes[0];
    }

    variantFor(product, size = this.currentSize()) {
      if (!size || !product?.variants) return null;
      const needle = sizeKey(size.optionMatch || size.label);
      if (!needle) return null;

      return (
        product.variants.find((variant) =>
          [variant.title, ...(variant.options || [])].some((part) => sizeKey(part) === needle)
        ) || null
      );
    }

    metafieldKeys(filter) {
      return String(filter?.metafield || '')
        .split(',')
        .map((key) => key.trim())
        .filter(Boolean);
    }

    metafieldLabel(key) {
      const slug = String(key || '').split('.').pop() || '';
      return slug.replace(/_/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());
    }

    filterGroups(filter) {
      const parsed = parseGroupRules(filter?.groupRules);
      if (parsed.length) return parsed;
      const key = String(filter?.groupMetafield || filter?.metafield || '').toLowerCase();
      const label = String(filter?.label || '').toLowerCase();
      if (key.includes('continent')) return DEFAULT_CONTINENT_RULES;
      if (key.includes('processing') || label === 'processing') return DEFAULT_PROCESS_RULES;
      return [];
    }

    filterOtherLabel(filter) {
      if (filter?.otherLabel != null && String(filter.otherLabel).trim() !== '') {
        return String(filter.otherLabel).trim();
      }
      const key = String(filter?.groupMetafield || filter?.metafield || '').toLowerCase();
      const label = String(filter?.label || '').toLowerCase();
      if (key.includes('processing') || label === 'processing') return 'Other';
      return '';
    }

    filterGroupKind(filter, metafieldKey) {
      const mode = String(filter?.groupMode || 'auto').toLowerCase();
      if (mode === 'off') return '';
      const applyTo = String(filter?.groupMetafield || '').trim().toLowerCase();
      const key = String(metafieldKey || '').toLowerCase();
      const keys = this.metafieldKeys(filter);
      if (keys.length > 1 && key) {
        if (applyTo) {
          if (key !== applyTo) return '';
        } else if (!key.includes('continent')) {
          return '';
        }
      } else if (applyTo && key && key !== applyTo) {
        return '';
      }
      if (mode === 'on') return this.filterGroups(filter).length ? 'grouped' : '';
      const haystack = String(metafieldKey || filter?.metafield || '').toLowerCase();
      const label = String(filter?.label || '').toLowerCase();
      if (haystack.includes('continent') && (!key || key.includes('continent'))) return 'grouped';
      if (haystack.includes('processing') || label === 'processing') return 'grouped';
      return '';
    }

    mapGroupedValue(filter, value) {
      const text = compactKey(value);
      if (!text) return '';
      const group = this.filterGroups(filter).find((item) =>
        item.keywords.some((keyword) => {
          const needle = compactKey(keyword);
          return needle && text.includes(needle);
        })
      );
      if (group) return group.label;
      return this.filterOtherLabel(filter);
    }

    filterValues(product, filter, metafieldKey) {
      const kind = this.filterGroupKind(filter, metafieldKey);
      const raw = this.productAttrs(product, filter, metafieldKey);
      if (!kind) return raw.filter((value) => value && !this.isSoldOutLabel(value));
      return raw.map((value) => this.mapGroupedValue(filter, value)).filter(Boolean);
    }

    productAttrs(product, filter, metafieldKey) {
      if (!filter || !filter.source) {
        return metafieldKey ? asList(product.metafields?.[metafieldKey]) : [];
      }
      if (filter.source === 'metafield') {
        const keys = metafieldKey ? [metafieldKey] : this.metafieldKeys(filter);
        return keys.flatMap((key) => asList(product.metafields?.[key]));
      }
      if (filter.source === 'tag_prefix') {
        const prefix = String(filter.tagPrefix || '').toLowerCase();
        return (product.tags || [])
          .filter((tag) => tag.toLowerCase().startsWith(prefix))
          .map((tag) => tag.slice(prefix.length).replace(/^[:\-\s]+/, '').trim())
          .filter(Boolean);
      }
      if (filter.source === 'tag') {
        const allowed = (filter.values || []).map((value) => value.toLowerCase());
        return (product.tags || []).filter((tag) => {
          if (!allowed.length) return true;
          return allowed.includes(tag.toLowerCase());
        });
      }
      return asList(product.metafields?.[filter.metafield]);
    }

    isExcluded(product) {
      return (product.tags || []).some((tag) => this.excludeTags.includes(normalize(tag)));
    }

    isAvailable(product) {
      const variant = this.variantFor(product);
      return Boolean(variant && variant.available !== false);
    }

    isSoldOutLabel(value) {
      return /^sold[\s-]*out$/i.test(String(value || '').trim());
    }

    sizedProducts() {
      const size = this.currentSize();
      if (!size) return [];
      return this.products.filter((product) => {
        if (this.isExcluded(product)) return false;
        return Boolean(this.variantFor(product, size));
      });
    }

    availableProducts() {
      return this.sizedProducts().filter((product) => this.isAvailable(product));
    }

    matchesFilters(product) {
      const query = this.state.search.trim().toLowerCase();
      if (query) {
        const haystack = [product.title, product.handle, ...(product.tags || [])].join(' ').toLowerCase();
        if (!haystack.includes(query)) return false;
      }

      return this.filters.every((filter) => {
        const selected = this.state.selected[filter.id] || [];
        if (!selected.length) return true;

        const keys = filter.source === 'metafield' ? this.metafieldKeys(filter) : [];
        if (keys.length > 1) {
          return keys.every((key) => {
            const groupOptions = this.filterOptions(filter, key).map((option) => option.label.toLowerCase());
            const selectedInGroup = selected.filter((item) => groupOptions.includes(item.toLowerCase()));
            if (!selectedInGroup.length) return true;
            const values = this.filterValues(product, filter, key).map((value) => value.toLowerCase());
            return selectedInGroup.some((item) => values.includes(item.toLowerCase()));
          });
        }

        const values = this.filterValues(product, filter).map((value) => value.toLowerCase());
        return selected.some((item) => values.includes(item.toLowerCase()));
      });
    }

    visibleProducts() {
      return this.availableProducts().filter((product) => this.matchesFilters(product));
    }

    filterOptions(filter, metafieldKey) {
      const kind = this.filterGroupKind(filter, metafieldKey);
      const counts = new Map();
      const whitelist = (filter.values || []).filter(Boolean);

      this.availableProducts().forEach((product) => {
        this.filterValues(product, filter, metafieldKey).forEach((value) => {
          if (!kind && whitelist.length && !whitelist.some((item) => item.toLowerCase() === value.toLowerCase())) {
            return;
          }
          counts.set(value, (counts.get(value) || 0) + 1);
        });
      });

      const ordered =
        kind === 'grouped'
          ? [
              ...this.filterGroups(filter).map((group) => group.label),
              this.filterOtherLabel(filter),
            ].filter(Boolean)
          : whitelist.length
            ? whitelist.filter((value) =>
                [...counts.keys()].some((key) => key.toLowerCase() === value.toLowerCase())
              )
            : [...counts.keys()].sort((a, b) => a.localeCompare(b));

      return ordered
        .filter((label) => (counts.get(label) || counts.get(
          [...counts.keys()].find((key) => key.toLowerCase() === String(label).toLowerCase())
        ) || 0) > 0)
        .map((label) => {
          const actual = [...counts.keys()].find((key) => key.toLowerCase() === String(label).toLowerCase()) || label;
          return { label: actual, count: counts.get(actual) || 0 };
        });
    }

    trayQty() {
      return this.state.tray.length;
    }

    trayCountFor(productId) {
      return this.state.tray.filter((item) => String(item.productId) === String(productId)).length;
    }

    traySubtotal() {
      return this.state.tray.reduce((sum, item) => sum + Number(item.price || 0), 0);
    }

    activeTier() {
      const size = this.currentSize();
      const qty = this.trayQty();
      let current = null;
      (size?.tiers || []).forEach((tier) => {
        if (qty >= tier.qty) current = tier;
      });
      return current;
    }

    nextTier() {
      const size = this.currentSize();
      const qty = this.trayQty();
      return (size?.tiers || []).find((tier) => qty < tier.qty) || null;
    }

    currentGoal() {
      const size = this.currentSize();
      const qty = this.trayQty();
      const min = Number(size?.minQty || 0);
      const tiers = size?.tiers || [];
      const first = tiers[0];
      const firstTarget = first ? Math.max(min, Number(first.qty) || 0) : min;

      if (firstTarget > 0 && qty < firstTarget) {
        return {
          remain: firstTarget - qty,
          percent: Number(first?.percent || 0),
          target: firstTarget,
        };
      }

      const next = this.nextTier();
      if (next) {
        return {
          remain: next.qty - qty,
          percent: Number(next.percent || 0),
          target: next.qty,
        };
      }

      return null;
    }

    fillProgress(template, count, percent) {
      return String(template || '')
        .replaceAll('[count]', String(count))
        .replaceAll('[percent]', String(percent))
        .replaceAll('{{ count }}', String(count))
        .replaceAll('{{ percent }}', String(percent));
    }

    persist() {
      writeStorage({ sizeId: this.state.sizeId, tray: this.state.tray });
    }

    addProduct(productId) {
      const product = this.products.find((item) => String(item.id) === String(productId));
      const variant = this.variantFor(product);
      if (!product || !variant || variant.available === false) return;

      if (!Array.isArray(this.state.tray)) {
        this.state.tray = normalizeTray(this.state.tray);
      }

      this.state.tray = [
        ...this.state.tray,
        {
          rowId: newRowId(),
          productId: product.id,
          variantId: variant.id,
          title: product.title,
          image: product.image,
          price: variant.price,
        },
      ];
      this.persist();
      this.render();
    }

    removeTrayRow(rowId) {
      this.state.tray = this.state.tray.filter((item) => String(item.rowId) !== String(rowId));
      this.persist();
      this.render();
    }

    changeSize(sizeId) {
      if (sizeId === this.state.sizeId) return;
      const qty = this.trayQty();
      if (qty > 0) {
        const ok = window.confirm(
          this.config.strings.sizeSwitchConfirm ||
            'A bundle can only contain one bag size. Clear this tray and switch size?'
        );
        if (!ok) return;
      }
      this.state.sizeId = sizeId;
      this.state.tray = [];
      this.persist();
      this.render();
    }

    selectedFilterTags() {
      return this.filters.flatMap((filter) =>
        (this.state.selected[filter.id] || []).map((value) => ({
          filterId: filter.id,
          value,
        }))
      );
    }

    toggleFilter(filterId, value) {
      const selected = this.state.selected[filterId] || [];
      const exists = selected.some((item) => item.toLowerCase() === value.toLowerCase());
      this.state.selected[filterId] = exists
        ? selected.filter((item) => item.toLowerCase() !== value.toLowerCase())
        : [...selected, value];
      this.render();
    }

    clearFilters() {
      this.filters.forEach((filter) => {
        this.state.selected[filter.id] = [];
      });
      this.state.search = '';
      this.render();
    }

    progressCopy() {
      const goal = this.currentGoal();
      const current = this.activeTier();
      if (goal) {
        const min = Number(this.currentSize()?.minQty || 0);
        const template =
          this.trayQty() < min
            ? this.config.strings.addMoreForMin
            : this.config.strings.addMoreForTier;
        return this.fillProgress(template, goal.remain, goal.percent);
      }
      if (current) {
        return this.fillProgress(this.config.strings.maxTier, this.trayQty(), current.percent);
      }
      return '';
    }

    discountHintHtml() {
      const goal = this.currentGoal();
      const current = this.activeTier();
      if (goal) {
        return `Add <strong class="sbb-discount__hint-count">${this.escape(String(goal.remain))}</strong> more for <strong class="sbb-discount__hint-pct">${this.escape(String(goal.percent))}%</strong> off`;
      }
      if (current) {
        return `You unlocked <strong class="sbb-discount__hint-pct">${this.escape(String(current.percent))}%</strong> off`;
      }
      return 'Add coffees to unlock a bundle discount';
    }

    async addBundleToCart() {
      const size = this.currentSize();
      const qty = this.trayQty();
      const min = Number(size?.minQty || 0);
      const button = this.querySelector('[data-add-bundle]');
      if (!size || qty < min) return;

      if (!size.uniqueRef) {
        window.alert(this.config.strings.missingRef || 'Add the Appstle unique ref on this bag size in the theme editor.');
        return;
      }

      const grouped = new Map();
      this.state.tray.forEach((item) => {
        const id = Number(item.variantId);
        const current = grouped.get(id);
        if (current) {
          current.quantity += 1;
          return;
        }
        grouped.set(id, {
          id,
          quantity: 1,
          properties: {
            '_appstle-bb-id': size.uniqueRef,
            '_appstle_bundles_type': this.config.bundleType || 'VOLUME_DISCOUNT',
            '__appstle-bb-name': size.checkoutName || size.label,
          },
        });
      });
      const items = Array.from(grouped.values());
      const rule = this.matchAppstleRule(size);

      const setAddLabel = (label, disabled) => {
        if (!button) return;
        button.disabled = Boolean(disabled);
        const surface = button.querySelector('.push-btn__surface');
        if (surface) {
          surface.textContent = label;
          return;
        }
        button.innerHTML = `<span class="push-btn__surface">${this.escape(label)}</span>`;
      };

      setAddLabel(this.config.strings.adding || 'Adding…', true);

      try {
        if (this.shouldClearCart(rule)) {
          const clearUrl = window.theme?.routes?.cart_clear_url || '/cart/clear.js';
          await fetch(clearUrl, {
            method: 'POST',
            headers: { Accept: 'application/json' },
          });
        }

        const addUrl = window.theme?.routes?.cart_add_url || '/cart/add.js';
        const response = await fetch(addUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items }),
        });
        if (!response.ok) {
          const payload = await response.json().catch(() => ({}));
          throw new Error(payload.description || payload.message || 'Could not add bundle');
        }
        this.state.tray = [];
        this.persist();
        document.dispatchEvent(new CustomEvent('theme:update:cart'));

        const destination = this.afterAddDestination(rule);
        if (destination.type === 'url') {
          window.location.href = destination.href;
          return;
        }
        if (destination.type === 'drawer') {
          let opened = false;
          const openDrawer = () => {
            if (opened) return;
            opened = true;
            this.openCartDrawer();
          };
          document.addEventListener('shapes:cart:update', openDrawer, { once: true });
          window.setTimeout(openDrawer, 450);
        }

        setAddLabel(
          `${this.config.strings.addBundle || 'Add bundle to cart'} (${qty} coffees)`,
          false
        );
        this.render();
      } catch (error) {
        window.alert(error.message || this.config.strings.cartError || 'Could not add bundle to cart.');
        setAddLabel(
          `${this.config.strings.addBundle || 'Add bundle to cart'} (${qty} coffees)`,
          false
        );
        this.render();
      }
    }

    bind() {
      this.addEventListener('click', (event) => {
        const sizeBtn = event.target.closest('[data-size-id]');
        if (sizeBtn) {
          this.changeSize(sizeBtn.getAttribute('data-size-id'));
          return;
        }
        const chip = event.target.closest('[data-filter-id]');
        if (chip) {
          this.toggleFilter(chip.getAttribute('data-filter-id'), chip.getAttribute('data-filter-value'));
          return;
        }
        const clear = event.target.closest('[data-clear-filters]');
        if (clear) {
          this.clearFilters();
          return;
        }
        const productCard = event.target.closest('[data-product-toggle]');
        if (productCard) {
          this.addProduct(productCard.getAttribute('data-product-toggle'));
          return;
        }
        const trayRemove = event.target.closest('[data-tray-remove]');
        if (trayRemove) {
          this.removeTrayRow(trayRemove.getAttribute('data-tray-remove'));
          return;
        }
        const scrollTray = event.target.closest('[data-scroll-tray]');
        if (scrollTray) {
          this.querySelector('[data-bundle-panel]')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
          return;
        }
        const addBundle = event.target.closest('[data-add-bundle]');
        if (addBundle) {
          this.addBundleToCart();
        }
      });

      this.addEventListener('input', (event) => {
        if (event.target.matches('[data-bundle-search]')) {
          this.state.search = event.target.value;
          this.render();
          const input = this.querySelector('[data-bundle-search]');
          if (input) {
            input.focus();
            input.setSelectionRange(input.value.length, input.value.length);
          }
        }
      });
    }

    render() {
      const size = this.currentSize();
      const visible = this.visibleProducts();
      const qty = this.trayQty();
      const subtotal = this.traySubtotal();
      const tier = this.activeTier();
      const discount = tier ? Math.round(subtotal * (tier.percent / 100)) : 0;
      const discountedTotal = Math.max(0, subtotal - discount);
      const min = Number(size?.minQty || 0);
      const canAdd = qty >= min && qty > 0;

      const chipButtons = (filter, options) =>
        options
          .map((option) => {
            const active = (this.state.selected[filter.id] || []).some(
              (item) => item.toLowerCase() === option.label.toLowerCase()
            );
            return `<button type="button" class="sbb-chip${active ? ' is-active' : ''}" data-filter-id="${filter.id}" data-filter-value="${this.escapeAttr(option.label)}">${this.escape(option.label)} <span class="sbb-chip__count">${option.count}</span></button>`;
          })
          .join('');

      const filterHeadingText = (filter) => {
        const heading = String(filter.filterHeading || filter.header || '').trim();
        if (heading) return heading;
        if (/^coffee type$/i.test(String(filter.label || '').trim())) {
          return 'Filter coffee types (not required)';
        }
        return '';
      };

      const filtersHtml = this.filters
        .map((filter) => {
          const keys = filter.source === 'metafield' ? this.metafieldKeys(filter) : [];
          const heading = filterHeadingText(filter);
          const headingHtml = heading
            ? `<p class="sbb-panel__title sbb-filter__heading">${this.escape(heading)}</p>`
            : '';
          const label = String(filter.label || '').trim();
          const titleHtml = label ? `<h3 class="sbb-filter__title">${this.escape(label)}</h3>` : '';
          const wrap = (inner) =>
            `<div class="sbb-filter-block">${headingHtml}<div class="sbb-filter">${titleHtml}${inner}</div></div>`;
          if (keys.length > 1) {
            const groups = keys
              .map((key) => {
                const options = this.filterOptions(filter, key);
                if (!options.length) return '';
                return `<div class="sbb-filter__chips">${chipButtons(filter, options)}</div>`;
              })
              .filter(Boolean);
            if (!groups.length) return '';
            return wrap(`<div class="sbb-filter__group">${groups.join('')}</div>`);
          }

          const options = this.filterOptions(filter);
          if (!options.length) return '';
          return wrap(`<div class="sbb-filter__chips">${chipButtons(filter, options)}</div>`);
        })
        .join('');

      const sizesHtml = this.sizes
        .map((item) => {
          const maxPercent = item.tiers.length ? item.tiers[item.tiers.length - 1].percent : 0;
          const firstPercent = item.tiers[0]?.percent || 0;
          const active = item.id === size?.id;
          const parts = String(item.label || '').trim().split(/\s+/);
          const amount = parts[0] || item.label;
          const unit = parts.slice(1).join(' ') || '';
          return `<button type="button" class="sbb-size${active ? ' is-active' : ''}" data-size-id="${item.id}">
            <span class="sbb-size__label">${this.escape(amount)}${unit ? ` <span class="sbb-size__unit">${this.escape(unit)}</span>` : ''}</span>
            <span class="sbb-size__meta">Min ${item.minQty} items<br>Save ${firstPercent}%</span>
          </button>`;
        })
        .join('');

      const tagsHtml = this.selectedFilterTags()
        .map(
          (tag) =>
            `<button type="button" class="sbb-tag" data-filter-id="${tag.filterId}" data-filter-value="${this.escapeAttr(tag.value)}">${this.escape(tag.value)} <span class="sbb-chip__count" aria-hidden="true">×</span></button>`
        )
        .join('');

      const cardsHtml = visible
        .map((product) => {
          const image = product.image
            ? `<img src="${this.escapeAttr(product.image)}" alt="${this.escapeAttr(product.title)}" width="400" height="400" loading="lazy">`
            : `<div class="sbb-card__placeholder"></div>`;
          const origin =
            asList(product.metafields?.[this.config.originMetafield || 'custom.country'])[0] || '';
          const process =
            asList(product.metafields?.[this.config.processMetafield || 'custom.processing_method'])[0] || '';
          const inTray = this.trayCountFor(product.id);
          return `<button type="button" class="sbb-card${inTray ? ' is-in-tray' : ''}" data-product-toggle="${product.id}">
            <div class="sbb-card__media">${image}${
              inTray ? `<span class="sbb-card__qty">× ${inTray}</span>` : ''
            }</div>
            <div class="sbb-card__body">
              <p class="sbb-card__meta"><span>${this.escape(origin)}</span><span>${this.escape(process)}</span></p>
              <h3 class="sbb-card__title">${this.escape(product.title)}</h3>
            </div>
          </button>`;
        })
        .join('');

      const trayHtml = this.state.tray
        .map((item) => {
          const thumb = item.image
            ? `<img class="sbb-tray__thumb" src="${this.escapeAttr(item.image)}" alt="" width="36" height="36">`
            : `<span class="sbb-tray__thumb"></span>`;
          return `<li class="sbb-tray__item">
            <div class="sbb-tray__pick">
              ${thumb}
              <span class="sbb-tray__name">${this.escape(item.title)}</span>
              <button type="button" class="sbb-tray__remove" data-tray-remove="${this.escapeAttr(item.rowId)}" aria-label="Remove ${this.escapeAttr(item.title)}">
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true" focusable="false">
                  <path d="M3 3l8 8M11 3L3 11" stroke="currentColor" stroke-width="1.6" stroke-linecap="square"/>
                </svg>
              </button>
            </div>
          </li>`;
        })
        .join('');

      const slotIcon = `<svg class="sbb-slot__icon" width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false"><g opacity="0.2"><path d="M14.5863 21.6591C18.3206 20.6585 20.189 15.5228 18.7596 10.1881C17.3302 4.85347 13.1442 1.34002 9.40992 2.34061C5.67566 3.3412 3.80721 8.47694 5.23663 13.8116C6.66605 19.1463 10.852 22.6597 14.5863 21.6591Z" stroke="#0E2A3D"/><path d="M9 4C11 8 11 16 9 20" stroke="#0E2A3D" stroke-width="0.75" stroke-linecap="round"/></g></svg>`;
      const goal = this.currentGoal();
      const slotCount = goal ? goal.remain : 0;
      const progress = this.progressCopy();
      const slotHtml = Array.from({ length: slotCount }, () => {
        const match = progress.match(/^(.*?)(\s+for\s+.*)$/i);
        const copy = match
          ? `<span class="sbb-slot__count">${this.escape(match[1].trim())}</span> <span class="sbb-slot__tier">${this.escape(match[2].trim())}</span>`
          : this.escape(progress);
        return `<li class="sbb-slot">${slotIcon}<span class="sbb-slot__copy">${copy}</span></li>`;
      }).join('');
      const barPct = min > 0 ? Math.min(100, Math.round((qty / (size?.tiers?.[size.tiers.length - 1]?.qty || min)) * 100)) : 0;

      const root = this.querySelector('[data-bundle-root]');
      if (!root) return;
      root.innerHTML = `
        <div class="sbb-layout">
          <div class="sbb-main">
            <div class="sbb-search">
              <label class="sr-only" for="sbb-search-${this.config.sectionId}">Search</label>
              <input id="sbb-search-${this.config.sectionId}" type="search" data-bundle-search placeholder="${this.escapeAttr(this.config.strings.searchPlaceholder || 'Search...')}" value="${this.escapeAttr(this.state.search)}">
            </div>
            <div class="sbb-filters">${filtersHtml}</div>
            <header class="sbb-header">
              <div class="sbb-header__start">
                <p class="sbb-count">${visible.length} ${this.escape(this.config.strings.coffeesMatch || 'coffees match')}</p>
                ${tagsHtml}
              </div>
              ${this.selectedFilterTags().length
                ? `<button type="button" class="sbb-clear" data-clear-filters>${this.escape(this.config.strings.clearAll || 'Clear all')}</button>`
                : ''}
            </header>
            <div class="sbb-grid">${cardsHtml || `<p class="sbb-empty">${this.escape(this.config.strings.empty || 'No coffees match this size and filters.')}</p>`}</div>
          </div>
          <aside class="sbb-sidebar" data-bundle-panel>
            <div class="sbb-panel">
              <h3 class="sbb-panel__title sbb-panel__title--label">${this.escape(this.config.strings.bagSize || 'Bag size')}</h3>
              <div class="sbb-sizes">${sizesHtml}</div>
              <div class="sbb-checkout">
                <div class="sbb-discount">
                  <p class="sbb-discount__label">${this.escape(this.config.strings.runningDiscount || 'Running discount')}</p>
                  <p class="sbb-discount__value">${money(discount)} <span>(${tier ? tier.percent : 0}%)</span></p>
                  <p class="sbb-discount__hint${this.currentGoal() ? '' : ' sbb-discount__hint--max'}">${this.discountHintHtml()}</p>
                  <div class="sbb-discount__bar" aria-hidden="true"><span style="width:${barPct}%"></span></div>
                </div>
                <div class="sbb-totals">
                  <div class="sbb-totals__row">
                    <span>Subtotal</span>
                    <span${discount > 0 ? ' class="sbb-totals__original"' : ''}>${money(subtotal)}</span>
                  </div>
                  <div class="sbb-totals__row sbb-totals__row--discounted">
                    <span>Total</span>
                    <span>${money(discountedTotal)}</span>
                  </div>
                </div>
                <button type="button" class="push-btn sbb-add" data-add-bundle ${canAdd ? '' : 'disabled'}>
                  <span class="push-btn__surface">${this.escape(
                    canAdd
                      ? `${this.config.strings.addBundle || 'Add bundle to cart'} (${qty} coffees)`
                      : this.config.strings.addMoreCoffees || 'Add more coffees'
                  )}</span>
                </button>
              </div>
              <div class="sbb-tray">
                <h3 class="sbb-panel__title">${this.escape(this.config.strings.yourBundle || 'Your bundle')}</h3>
                <p class="sbb-size-status">${this.escape(size?.label || '')} bags · Min ${min} coffees</p>
                <ul class="sbb-tray__list">${trayHtml}${slotHtml || (trayHtml ? '' : `<li class="sbb-tray__empty">${this.escape(this.config.strings.trayEmpty || 'Add coffees to start your bundle.')}</li>`)}</ul>
              </div>
            </div>
          </aside>
        </div>
        <button type="button" class="sbb-dock${qty ? ' is-visible' : ''}" data-scroll-tray ${qty ? '' : 'hidden'}>
          <span class="sbb-dock__copy">Your bundle · ${qty} ${qty === 1 ? 'coffee' : 'coffees'}</span>
          <span class="sbb-dock__meta">${tier ? tier.percent : 0}%</span>
        </button>
      `;
    }

    escape(value) {
      return String(value || '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
    }

    escapeAttr(value) {
      return this.escape(value).replace(/"/g, '&quot;');
    }
  }

  if (!customElements.get('showroom-bundle-builder')) {
    customElements.define('showroom-bundle-builder', ShowroomBundleBuilder);
  }
})();
